import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { ModalHandler } from "../core/modal-handler";
import { PortalService } from "./portal-service.service";
import { UserDetails } from "../models/user";
import { AuthService } from "./auth.service";
import { BrowserStorage } from "../core/storage";
import { Contracts } from "../models/contracts";
import { Router } from "@angular/router";
import { CristieUtils } from "../core/utils.service";

@Injectable({
  providedIn: "root",
})
export class ShellService {
  public contracts: Contracts[] = null;
  public currentUser: UserDetails;
  public selectedContractId: number = null;
  private readonly selectedContractIdSubject = new BehaviorSubject<number | null>(null);
  private readonly selectedContractOwnerKey = "shell.selectedContractOwner";
  constructor(
    private ps: PortalService,
    private mh: ModalHandler,
    private as: AuthService,
    private router: Router,
  ) {}

  async populateInterface() {
    this.resetInterface();

    if (this.isTrialSession()) {
      this.currentUser = this.getStoredTrialUserDetails();
      return;
    }

    await this.populateContracts();
  }

  async populateContracts() {
    try {
      const token = this.as.getToken();
      const userId = this.as.JWTDecode(token).sub;
      this.resetRememberedContractForDifferentUser(userId);

      this.contracts = await this.ps.getContracts();
      const storedSelectedContractId = BrowserStorage.getInt("shell.selectedContractId", false);
      const activeContracts = this.contracts.filter((contract) => contract.active);

      if (this.contracts.length > 0) {
        const preferredContractId = this.selectedContractId ?? storedSelectedContractId;
        const preferredContract = this.contracts.find((contract) => contract.id === preferredContractId && contract.active);
        this.selectedContractId = preferredContract?.id ?? activeContracts[0]?.id ?? this.contracts[0].id;
        this.persistSelectedContract(this.selectedContractId, userId);
      } else {
        this.clearSelectedContractState();
      }

      this.currentUser = await this.ps.getUser(userId);
    } catch (error) {
      if (this.isUnauthorized(error)) {
        this.resetInterface();
        this.as.logout();
        await this.router.navigate(["/login"]);
        return;
      }

      this.mh.throwError("Failed to populate interface", error);
    }
  }

  setSelectedContract(id: number | null) {
    const userId = this.as.JWTDecode(this.as.getToken()).sub;
    if (id != null) this.persistSelectedContract(id, userId);
    else this.clearSelectedContractState();
  }

  get selectedContractIdChanges() {
    return this.selectedContractIdSubject.asObservable();
  }

  get selectedContract(): Contracts | null {
    if (this.contracts == null || this.contracts.length == 0) return null;
    return (
      this.contracts.find((contract) => contract.id === this.selectedContractId) ||
      this.contracts.find((contract) => contract.active) ||
      this.contracts[0]
    );
  }

  resetInterface(): void {
    this.contracts = null;
    this.currentUser = null;
    this.selectedContractId = null;
    this.selectedContractIdSubject.next(null);
  }

  private isTrialSession(): boolean {
    return !!this.as.JWTDecode(this.as.getToken()).trial;
  }

  private persistSelectedContract(contractId: number | null, userId: string | number | undefined): void {
    this.selectedContractId = contractId;
    if (contractId != null) {
      BrowserStorage.setInt("shell.selectedContractId", contractId, false);
      if (userId != null) BrowserStorage.setString(this.selectedContractOwnerKey, String(userId), false);
    } else {
      this.clearSelectedContractState();
      return;
    }

    this.selectedContractIdSubject.next(this.selectedContractId);
  }

  private clearSelectedContractState(): void {
    this.selectedContractId = null;
    BrowserStorage.delete("shell.selectedContractId", false);
    BrowserStorage.delete(this.selectedContractOwnerKey, false);
    this.selectedContractIdSubject.next(null);
  }

  private resetRememberedContractForDifferentUser(userId: string | number | undefined): void {
    const storedOwner = BrowserStorage.getString(this.selectedContractOwnerKey, false);
    if (storedOwner && userId != null && storedOwner !== String(userId)) {
      this.clearSelectedContractState();
    }
  }

  private getStoredTrialUserDetails(): UserDetails {
    const user = new UserDetails();
    try {
      user.username = CristieUtils.decodeBase64(localStorage.getItem("name") || "");
      user.email = CristieUtils.decodeBase64(localStorage.getItem("email") || "");
    } catch {
      user.username = "";
      user.email = "";
    }
    return user;
  }

  private isUnauthorized(error: any): boolean {
    return Number(error?.httpErrorCode ?? error?.status) === 401 || error?.description === "Unauthorized";
  }
}
